'use client'

import { Check, ChevronsUpDown, Loader2, X } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { AUTO_SAVE_FEEDBACK_MS, useAutoSave } from '@/hooks/use-auto-save'
import { cn } from '@/lib/utils'
import { Badge } from '../admin/shared/badge'
import { AutoSaveStatusIcon, autoSaveBorderClass } from './auto-save-status'
import { Button } from './button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './command'
import { Popover, PopoverContent, PopoverTrigger } from './popover'

/** One pending add/remove — the unit this control saves. */
interface ItemChange {
  action: 'add' | 'remove'
  item: string
}

// Stable default functions to avoid re-renders
const DEFAULT_OPTIONS: never[] = []
const defaultGetOptionValue = <T,>(opt: T): string => opt as string
const defaultGetOptionLabel = <T,>(opt: T): string => opt as string
const defaultGetOptionSearchText = <T,>(opt: T): string => opt as string

interface AutoSaveMultiSelectProps<T = string> {
  /** Current selected values */
  value: string[]
  /** Called when an item is added - should return a promise */
  onAdd: (item: string) => Promise<void>
  /** Called when an item is removed - should return a promise */
  onRemove: (item: string) => Promise<void>
  /** Available options to select from */
  options?: T[]
  /** Function to get the value from an option (for object arrays) */
  getOptionValue?: (option: T) => string
  /** Function to get the label from an option (for object arrays) */
  getOptionLabel?: (option: T) => string
  /** Function to get search text from an option (for object arrays) */
  getOptionSearchText?: (option: T) => string
  /** Placeholder for the input field */
  inputPlaceholder?: string
  /** Placeholder for the search/select button */
  selectPlaceholder?: string
  /** Label for the field */
  label?: string
  /** Whether to allow free-form text input (for tags) */
  allowFreeForm?: boolean
  /** Additional info to display next to label */
  labelInfo?: string
  /** Whether the component is disabled */
  disabled?: boolean
  /** Duration in ms for the success/error indicator to show */
  feedbackDurationMs?: number
  /** Custom badge variant */
  badgeVariant?: 'default' | 'topic' | 'secondary' | 'destructive' | 'outline'
}

/**
 * A multi-select component that auto-saves on add/remove with visual feedback.
 * Shows a green border animation on successful save, and a red border on error.
 * Supports both free-form input (tags) and selection from options (companies).
 */
export function AutoSaveMultiSelect<T = string>({
  value,
  onAdd,
  onRemove,
  options = DEFAULT_OPTIONS as T[],
  getOptionValue = defaultGetOptionValue,
  getOptionLabel = defaultGetOptionLabel,
  getOptionSearchText = defaultGetOptionSearchText,
  inputPlaceholder = 'Add item and press Enter',
  selectPlaceholder = 'Search...',
  label,
  allowFreeForm = true,
  labelInfo,
  disabled = false,
  feedbackDurationMs = AUTO_SAVE_FEEDBACK_MS,
  badgeVariant = 'topic',
}: AutoSaveMultiSelectProps<T>) {
  const [isOpen, setIsOpen] = useState(false)
  const [inputValue, setInputValue] = useState('')
  // The item currently being saved, so its badge can show its own spinner.
  const [activeChange, setActiveChange] = useState<ItemChange | null>(null)

  // Every save carries its own payload, so the hook needs no ambient `value`.
  const { state: saveState, flush } = useAutoSave<ItemChange>({
    onSave: (change) =>
      change.action === 'add' ? onAdd(change.item) : onRemove(change.item),
    feedbackMs: feedbackDurationMs,
    onSaved: (change) => {
      if (change.action === 'add') {
        setInputValue('')
        setIsOpen(false)
      }
    },
    logLabel: 'AutoSaveMultiSelect',
  })

  // Drop the per-item highlight once the feedback window closes.
  useEffect(() => {
    if (saveState === 'idle') {
      setActiveChange(null)
    }
  }, [saveState])

  const saveChange = useCallback(
    (change: ItemChange) => {
      setActiveChange(change)
      // Add/remove are deliberate discrete actions — save now, no debounce.
      flush(change)
    },
    [flush],
  )

  const handleAdd = useCallback(
    (item: string) => {
      const trimmedItem = item.trim()
      if (!trimmedItem || value.includes(trimmedItem)) return
      saveChange({ action: 'add', item: trimmedItem })
    },
    [saveChange, value],
  )

  const handleRemove = useCallback(
    (item: string) => saveChange({ action: 'remove', item }),
    [saveChange],
  )

  // Filter available options (not already selected)
  const availableOptions = options.filter(
    (opt) => !value.includes(getOptionValue(opt)),
  )

  return (
    <div>
      {label && (
        <label className="text-muted-foreground mb-1 flex items-center gap-2 text-sm font-medium">
          {label}
          {labelInfo && (
            <span className="bg-muted text-muted-foreground rounded px-1.5 py-0.5 text-xs font-normal">
              {labelInfo}
            </span>
          )}
        </label>
      )}

      <div className="space-y-2">
        {/* Searchable dropdown for both modes */}
        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              role="combobox"
              aria-expanded={isOpen}
              disabled={disabled || saveState === 'saving'}
              className={cn(
                'w-full justify-between font-normal transition-colors duration-200',
                autoSaveBorderClass(saveState),
              )}
            >
              <span className="truncate">{inputPlaceholder}</span>
              <div className="ml-2 flex shrink-0 items-center gap-1">
                <AutoSaveStatusIcon state={saveState} className="h-4 w-4" />
                <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
              </div>
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-[400px] p-0"
            align="start"
            side="bottom"
            avoidCollisions={false}
          >
            <Command shouldFilter={!allowFreeForm}>
              <CommandInput
                placeholder={
                  allowFreeForm ? 'Type to add or search...' : selectPlaceholder
                }
                value={inputValue}
                onValueChange={setInputValue}
                onKeyDown={(e) => {
                  // Only handle Enter for free-form mode
                  // For non-free-form, let Command handle it to select the highlighted item
                  if (e.key === 'Enter' && allowFreeForm && inputValue.trim()) {
                    e.preventDefault()
                    handleAdd(inputValue)
                  }
                }}
              />
              <CommandList className="max-h-[300px]">
                {availableOptions.length === 0 && !allowFreeForm && (
                  <CommandEmpty>No options found.</CommandEmpty>
                )}
                {allowFreeForm &&
                  inputValue.trim() &&
                  !value.includes(inputValue.trim()) && (
                    <CommandGroup heading="Add new">
                      <CommandItem
                        value={inputValue}
                        onSelect={() => handleAdd(inputValue)}
                      >
                        <Check className="mr-2 h-4 w-4 opacity-0" />
                        <span>Add "{inputValue}"</span>
                      </CommandItem>
                    </CommandGroup>
                  )}
                {availableOptions.length > 0 && (
                  <CommandGroup
                    heading={allowFreeForm ? 'Suggestions' : undefined}
                  >
                    {availableOptions.map((option) => (
                      <CommandItem
                        key={getOptionValue(option)}
                        value={getOptionSearchText(option)}
                        onSelect={() => handleAdd(getOptionValue(option))}
                      >
                        <Check
                          className={cn(
                            'mr-2 h-4 w-4',
                            value.includes(getOptionValue(option))
                              ? 'opacity-100'
                              : 'opacity-0',
                          )}
                        />
                        <span className="truncate">
                          {getOptionLabel(option)}
                        </span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        {/* Selected items */}
        {value.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {value.map((item) => {
              const option = options.find((opt) => getOptionValue(opt) === item)
              const displayLabel = option ? getOptionLabel(option) : item
              const isItemSaving =
                activeChange?.item === item && saveState === 'saving'
              const isItemError =
                activeChange?.item === item && saveState === 'error'

              return (
                <Badge
                  key={item}
                  variant={badgeVariant}
                  className={cn(
                    'gap-1 pr-1 transition-colors duration-200',
                    isItemSaving && 'border-blue-500',
                    isItemError && 'border-red-500 bg-red-50 text-red-700',
                  )}
                >
                  {displayLabel}
                  <button
                    type="button"
                    onClick={() => handleRemove(item)}
                    disabled={disabled || saveState === 'saving'}
                    className="hover:text-foreground ml-1 disabled:opacity-50"
                  >
                    {isItemSaving ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <X size={12} />
                    )}
                  </button>
                </Badge>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

export type { AutoSaveMultiSelectProps }
